import { getBrowserContext } from "./browser";
import { getAttribution } from "./attribution";
import { safeStorage } from "./storage";

// Funnel events for the /start onboarding. Everything goes to the GTM dataLayer
// (GTM forwards it to GA4 and the Meta Pixel) and, once Intercom has booted, to
// Intercom as events. Nothing here may throw: an analytics failure must never
// break a step of the flow.

function pushDataLayer(payload) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  } catch {
    // ignore
  }
}

function eventContext() {
  const { platform, inAppBrowser } = getBrowserContext();
  return { onboarding_flow: "start", platform, in_app_browser: inAppBrowser || "none" };
}

// Intercom is booted by GTM, after the app. Calls made before it has booted are
// queued by the stub, but `update` before boot can land on the anonymous visitor
// rather than the signed-in user, so attribute writes wait for `booted`.
const POLL_MS = 200;
const BOOT_TIMEOUT_MS = 20000;

export function whenIntercomBooted(callback) {
  if (typeof window === "undefined") return () => {};
  let cancelled = false;
  const started = Date.now();
  const check = () => {
    if (cancelled) return;
    if (window.Intercom && window.Intercom.booted) {
      try {
        callback(window.Intercom);
      } catch {
        // ignore
      }
      return;
    }
    if (Date.now() - started < BOOT_TIMEOUT_MS) setTimeout(check, POLL_MS);
  };
  check();
  return () => {
    cancelled = true;
  };
}

export function track(event, props = {}) {
  const payload = { event, ...eventContext(), ...props };
  pushDataLayer(payload);
  try {
    if (typeof window !== "undefined" && typeof window.Intercom === "function") {
      // Intercom keeps at most ~10 metadata keys, so only the event's own props.
      window.Intercom("trackEvent", event, { ...props });
    }
  } catch {
    // ignore
  }
}

// Fires an event at most once per browser for the given key (e.g. a step that
// can be re-rendered or revisited after a reload).
export function trackOnce(key, event, props = {}) {
  const storageKey = `tracked:${key}`;
  if (safeStorage.get(storageKey)) return false;
  safeStorage.set(storageKey, true);
  track(event, props);
  return true;
}

// Durable per-person attributes on the Intercom user, so funnels can be split by
// onboarding flow and campaign without a backend change. Sent once per page
// load, after Intercom has identified the user.
let identified = false;
export function identifyStartFlowUser() {
  if (identified) return;
  identified = true;
  const { platform, inAppBrowser } = getBrowserContext();
  const attribution = getAttribution() || {};
  const attributes = {
    onboarding_flow: "start",
    signup_platform: platform,
    signup_in_app_browser: inAppBrowser || "none",
  };
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content"]) {
    if (attribution[key]) attributes[key] = attribution[key];
  }
  whenIntercomBooted((intercom) => intercom("update", attributes));
}

export function openSupportChat(message) {
  try {
    if (typeof window.Intercom === "function") {
      if (message) window.Intercom("showNewMessage", message);
      else window.Intercom("show");
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

// The exact payload Setup.jsx pushes on ?payment=success, so GTM conversion
// tags (GA4 purchase, Meta) count both onboarding flows the same way. Prices and
// shape are copied from that file; keep them in step with it. The only
// addition is onboarding_flow. Unlike Setup.jsx this waits for the Stripe
// webhook to fill in the plan instead of throwing while it is still null.
const PURCHASE_PRICES = {
  month: { starter: 19, growth: 39, advanced: 79 },
  year: { starter: 190, growth: 390, advanced: 790 },
};

export function buildPurchaseEvent(store) {
  const cycle = store?.billing_cycle;
  const plan = store?.current_plan;
  const value = PURCHASE_PRICES[cycle]?.[plan];
  if (!store?.stripe_subscription_id || value == null) return null;
  return {
    event: "purchase",
    transaction_id: store.stripe_subscription_id,
    value,
    currency: "EUR",
    items: [
      {
        item_id: `${plan}_${cycle}`,
        item_name: plan,
        price: value,
        quantity: 1,
        subscription_term: cycle,
      },
    ],
    onboarding_flow: "start",
  };
}

export function pushPurchaseOnce(store) {
  const payload = buildPurchaseEvent(store);
  if (!payload) return false;
  const key = `tracked:purchase:${payload.transaction_id}`;
  if (safeStorage.get(key)) return false;
  safeStorage.set(key, true);
  pushDataLayer(payload);
  return true;
}
